import { vec3 } from 'gl-matrix';
import getVoxelThicknessAlongNormal from '../getVoxelThicknessAlongNormal.js';
import { projectPointOntoPlane } from '../slabMembership.js';
import { createColumnLineResolver, createPlaneBasis, SHAPE_BOUNDARY_EPSILON, solveQuadraticLeqZero, toIntegerRun, } from './shapeGeometry.js';
const UNIT_BOUNDARY = 1 + SHAPE_BOUNDARY_EPSILON;
export function createEllipseShape(options) {
    const { volume, planePoint, viewPlaneNormal: normal, majorAxis, majorRadius, minorRadius, depthRadius, } = options;
    if (!(majorRadius > 0) || !(minorRadius > 0)) {
        throw new Error('Ellipse radii must be positive');
    }
    if (depthRadius !== undefined && !(depthRadius > 0)) {
        throw new Error('Ellipse depthRadius must be positive when supplied');
    }
    const basis = createPlaneBasis(normal, majorAxis);
    const isSolid = depthRadius !== undefined;
    const voxelThickness = getVoxelThicknessAlongNormal(volume, basis.n);
    const depthSemiAxis = isSolid
        ? depthRadius + voxelThickness / 2
        : 0;
    const center = isSolid
        ? [...options.centerWorld]
        : projectPointOntoPlane(options.centerWorld, planePoint, basis.n);
    const scaledAxes = [
        { axis: basis.u, inverseRadius: 1 / majorRadius },
        { axis: basis.v, inverseRadius: 1 / minorRadius },
    ];
    if (isSolid) {
        scaledAxes.push({ axis: basis.n, inverseRadius: 1 / depthSemiAxis });
    }
    const resolveColumnLine = createColumnLineResolver(volume, basis.n);
    function containsPoint(point) {
        const tested = isSolid
            ? point
            : projectPointOntoPlane(point, planePoint, basis.n);
        let total = 0;
        for (const { axis, inverseRadius } of scaledAxes) {
            const offset = (tested[0] - center[0]) * axis[0] +
                (tested[1] - center[1]) * axis[1] +
                (tested[2] - center[2]) * axis[2];
            const scaled = offset * inverseRadius;
            total += scaled * scaled;
        }
        return total <= UNIT_BOUNDARY;
    }
    function* getRuns(outerIndex, rowIndex, _depthRun, slab) {
        const line = resolveColumnLine(slab, outerIndex, rowIndex);
        const lineBase = isSolid ? line.base : line.projectedBase;
        const lineStep = isSolid ? line.step : line.projectedStep;
        let a = 0;
        let b = 0;
        let c = -UNIT_BOUNDARY;
        for (const { axis, inverseRadius } of scaledAxes) {
            const offset = ((lineBase[0] - center[0]) * axis[0] +
                (lineBase[1] - center[1]) * axis[1] +
                (lineBase[2] - center[2]) * axis[2]) *
                inverseRadius;
            const slope = (lineStep[0] * axis[0] +
                lineStep[1] * axis[1] +
                lineStep[2] * axis[2]) *
                inverseRadius;
            a += slope * slope;
            b += 2 * offset * slope;
            c += offset * offset;
        }
        const run = toIntegerRun(solveQuadraticLeqZero(a, b, c));
        if (run) {
            yield run;
        }
    }
    return {
        containsPoint,
        getRuns,
        getRequiredThickness: () => (isSolid ? depthSemiAxis * 2 : 0),
    };
}
export function createCircleShape(options) {
    const { viewPlaneNormal: normal } = options;
    const candidate = Math.abs(normal[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
    const majorAxis = vec3.cross(vec3.create(), normal, candidate);
    return createEllipseShape({
        volume: options.volume,
        planePoint: options.planePoint,
        viewPlaneNormal: normal,
        centerWorld: options.centerWorld,
        majorAxis,
        majorRadius: options.radius,
        minorRadius: options.radius,
        depthRadius: options.depthRadius,
    });
}
